import styles from './VideoAnalysisChart.module.less'
// import * as echarts from 'echarts/core'
import VideoPlayer from '../VideoPlayer/VideoPlayer'
import { useCallback, useContext, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { EChartsInstance } from 'echarts-for-react/lib/types'
// import { createPortal } from 'react-dom'
import { useEventListener, useRafState, useSize, useThrottleFn, } from 'ahooks'
// import FloatLine from './components/FloatLine/FloatLine'
// import FloatTooltip from './components/FloatTooltip/FloatTooltip'
import CoreChart from './components/CoreChart/CoreChart'
import { useChartConfig } from './components/CoreChart/CoreChart'
import VideoAnalysisChartCover from './components/VideoAnalysisChartCover/VideoAnalysisChartCover'
import LegendDot from './components/LegendDot/LegendDot'
import cls from 'classnames'
import { EChartsOption } from 'echarts'
import VideoController from './components/VideoController/VideoController'
import Player from 'xgplayer/es/player'
import ProgressLine from './components/ProgressLine/ProgressLine'
import LineDotMap from './components/LineDotMap/LineDotMap'
import ProgressDashLine from './components/ProgressDashLine/ProgressDashLine'
import ProgressDashLineViewPanel from './components/ProgressDashLineViewPanel/ProgressDashLineViewPanel'
import { VideoActionContext } from '../VideoAnalysis'
import { commonUtils } from '@/helpers/commonUtils'
import { useTranslation } from 'react-i18next'

interface ChartProps {
  onAxisClick?: (second: number) => void
  onAxisChange?: (index: number) => void
  project: ProjectModels.ProjectDetail | ProjectModels.ProjectDetailV3
  mode?: 'professor' | 'default'
  viewMode: 'realtime' | 'panoramic'
  orientation?: 'landscape' | 'portrait'
  layout?: 'default' | 'portrait-splash' | 'portrait-regular'
  chartWidth?: number
}

export function useParentRect(ref: React.MutableRefObject<HTMLDivElement | null>): DOMRect | null {
  const [parentRect, setParenRect] = useState<DOMRect | null>(null)
  const size = useSize(ref)
  useEffect(() => {
    if (!ref.current) { return }
    setParenRect(ref.current.getBoundingClientRect())
  }, [size])
  return parentRect
}

export function useLineHeight(
  detailMetrics: ProjectModels.DetailMetricItem[],
  parentRect: DOMRect | null
) {
  const [lineHeight, setLineHeight] = useState(0)
  const [lineTop, setLineTop] = useState(0)
  const { config } = useChartConfig(detailMetrics)

  useEffect(() => {
    if (!parentRect) { return }
    const chartOffsetConfigGrid = config.grid as any
    const containerHeight = parentRect!.height
    /** chartHeight 是指 y 轴的高度 */
    const yAxisHeight = containerHeight - chartOffsetConfigGrid.bottom - chartOffsetConfigGrid.top
    /** y 轴被平均分成 100 份 */
    const chartRatio = yAxisHeight / 100
    const scores = detailMetrics.map(m => [m.cognitiveEngagementScore, m.emotionRecognitionScore, m.eyeMovementRationScore]).flat()
    const maxScore = Math.max(...scores)
    const minScore = Math.min(...scores)
    /** 这里线上下都应该要有冗余，不能真的是最大值和最小值的高度 */
    const DELTA = 50
    const lineHeight = chartRatio * (maxScore - minScore) + DELTA * 2
    const maxY = parentRect!.height - maxScore * chartRatio
    setLineHeight(lineHeight)
    setLineTop(maxY - DELTA * 1.5)
  }, [parentRect])
  return { height: lineHeight, top: lineTop }
}


function VideoAnalysisChart(props: ChartProps) {
  const { lastestVideoInstances, setLastestVideoInstances, videoBlobUrl } = useContext(VideoActionContext)
  return <SubjectiveResponseCurveVideo
    project={props.project} viewMode={props.viewMode}
    orientation={props.orientation}
    layout={props.layout}
    chartWidth={props.chartWidth}
    videoBlobUrl={videoBlobUrl}
    onPlay={(instances) => {
      if (lastestVideoInstances.some(instance => !instances.includes(instance))) {
        lastestVideoInstances.forEach(instance => {
          instance.pause()
        })
      }
      setLastestVideoInstances(instances)
    }}
    onError={(instances) => {
      setLastestVideoInstances(instances)
    }} />
}

interface SubjectiveResponseCurveVideoProps extends ChartProps {
  videoBlobUrl: string | null
  onPlay?: (instances: Player[]) => void
  onError?: (instances: Player[]) => void
  clacSizeElement?: () => HTMLElement
}

const PORTRAIT_SPLASH_VIDEO_WIDTH = 300

export function SubjectiveResponseCurveVideo(props: SubjectiveResponseCurveVideoProps) {
  const { onPlay, onError, videoBlobUrl, clacSizeElement = () => document.body, project } = props
  const { t } = useTranslation()
  // const { lastestVideoInstances, setLastestVideoInstances, videoBlobUrl } = useContext(VideoActionContext)
  const componentId = useId()
  const [axisChartInstance, setAxisChartInstance] = useState<EChartsInstance | null>(null)
  const [chartInstance, setChartInstance] = useState<EChartsInstance | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [containerRect, setContainerRect] = useState<DOMRect | null>(null)
  const parentRect = useParentRect(containerRef)
  const { config } = useChartConfig(project.detailMetrics)
  const chartConfig = config as EChartsOption
  const { left: chartLeft, right: chartRight, top: chartTop, bottom: chartBottom } = chartConfig.grid as { left: number, right: number, top: number, bottom: number }
  const linePosition = useLineHeight(project.detailMetrics, parentRect)
  const hostSize = useSize(clacSizeElement)
  const containerSize = useSize(containerRef)
  const [chartHeight, setChartHeight] = useState(300)
  const [second, setSecond] = useState<number | undefined>(undefined)
  const [progress, setProgress] = useState<number | undefined>(undefined)
  const [videoSize, setVideoSize] = useState<{ width: string, height: string, left: string, top: string }>({
    width: '0px',
    height: '0px',
    left: '0px',
    top: '0px',
  })
  const [videoAspectRatio, setVideoAspectRatio] = useState<number | null>(null)
  const [videoInstance, setVideoInstance] = useState<Player | null>(null)
  const prevContainerWidthRef = useRef<number | undefined>(undefined)
  const resizeIdleTimerRef = useRef<number | undefined>(undefined)
  const [isResizing, setIsResizing] = useState(false)

  const [dragVideoController, setDragVideoController] = useState(false)
  const [isControllerMouseMove, setIsControllerMouseMove] = useState(false)

  const [cavansMoveX, setCavansMoveX] = useRafState(0)
  const isMouseInChartRef = useRef(false)
  const lastMousePosRef = useRef<{ x: number, clientX: number, clientY: number } | null>(null)

  const [selectedDimension, setSelectedDimension] = useState<string[]>([])
  const isPortraitSplashLayout = props.layout === 'portrait-splash'
  const isPortraitRegularLayout = props.layout === 'portrait-regular'
  const splashCoverWidth = videoSize.width
  const splashCoverTrackWidth =
    props.chartWidth !== undefined
      ? `${props.chartWidth}px`
      : containerRect?.width
        ? `${containerRect.width}px`
        : '100%'

  const [erroVideos, setErroVideos] = useState<Array<'video'>>([])
  const [xAxisDots, setXAxisDots] = useState<Array<{ x: number, dots: Array<{ x: number, y: number }> }>>([])
  const [activeXAxisIndex, setActiveXAxisIndex] = useState(-1)
  const VIDEO_CONTROLLER_ID = 'VIDEO_CONTROLLER_ID'
  const [vol, setVol] = useState(0)
  const chartRenderOptions = useMemo(() => ({ height: `${chartHeight}px`, dotSize: 0 }), [chartHeight])
  const axisCount = useMemo(() => {
    if (project.detailMetrics.length > 0) {
      return project.detailMetrics.length
    }
    return Math.max(1, Math.round(project.adDuration))
  }, [project.detailMetrics.length, project.adDuration])
  const progressLineX = useMemo(() => {
    if (progress === undefined) {
      return 0
    }
    const videoWidth = Number(videoSize.width.replace('px', ''))
    const baseLeft = Number(videoSize.left.replace('px', ''))
    if (!Number.isFinite(videoWidth) || videoWidth <= 0 || !Number.isFinite(baseLeft)) {
      return 0
    }
    const safeAxisCount = Math.max(1, axisCount || 1)
    const xAxisWidth = videoWidth / safeAxisCount
    const moveBaseX = baseLeft + xAxisWidth * 0.5
    return Math.round(moveBaseX + (videoWidth - xAxisWidth) * progress / 100)
  }, [axisCount, progress, videoSize.left, videoSize.width])
  const chartWidth = containerRect?.width || 0
  const dashLineX = useMemo(() => {
    if (cavansMoveX === 0) {
      return 0
    }
    const min = Number(videoSize.left.replace('px', ''))
    const width = Number(videoSize.width.replace('px', ''))
    if (!Number.isFinite(min) || !Number.isFinite(width) || width <= 0) {
      return 0
    }
    const max = min + width
    return cavansMoveX <= min
      ? min
      : cavansMoveX >= max
        ? max
        : cavansMoveX
  }, [cavansMoveX, videoSize.left, videoSize.width])
  const isNeedRealtimeReveal = props.viewMode === 'realtime' && progressLineX > 0
  const PROGRESS_LINE_WIDTH = 2
  const seriesClipWidth = isNeedRealtimeReveal
    ? Math.max(0, Math.min(chartWidth, progressLineX + PROGRESS_LINE_WIDTH))
    : chartWidth
  const hiddenRight = chartWidth > 0 ? Math.max(0, chartWidth - seriesClipWidth) : 0
  const seriesClipPath = isNeedRealtimeReveal ? `inset(0 ${hiddenRight}px 0 0)` : 'inset(0 0 0 0)'


  const handleVideoError = () => {
    if (videoInstance) {
      videoInstance.pause()
    }
    setErroVideos(['video'])
  }

  const handleSetAxisChartCustomConfig = useCallback((config: EChartsOption) => {
    return {
      ...config,
      xAxis: {
        ...config.xAxis,
        splitLine: {
          show: true,
          lineStyle: {
            color: 'rgba(238, 238, 238, 0.1)',
          }
        }
      },
      yAxis: {
        ...(config.yAxis as any),
        show: true,
        min: 0,
        max: 100,
        axisLabel: {
          ...((config.yAxis as any)?.axisLabel || {}),
          show: true,
          color: 'rgba(42, 44, 63, .85)',
        },
        axisLine: {
          ...((config.yAxis as any)?.axisLine || {}),
          show: true,
          lineStyle: {
            ...((config.yAxis as any)?.axisLine?.lineStyle || {}),
            color: 'rgba(42, 44, 63, .35)',
          }
        },
        axisTick: {
          ...((config.yAxis as any)?.axisTick || {}),
          show: true,
          lineStyle: {
            ...((config.yAxis as any)?.axisTick?.lineStyle || {}),
            color: 'rgba(42, 44, 63, .35)',
          }
        },
        splitLine: {
          ...((config.yAxis as any)?.splitLine || {}),
          show: false,
        }
      }
    }
  }, [])

  const handleSetSeriesChartCustomConfig = useCallback((config: EChartsOption) => {
    return {
      ...config,
      series: (config.series as any[]).filter(ser => selectedDimension.includes(ser.name)).map((s,) => {
        return {
          ...s,
          areaStyle: undefined
        }
      })
    }
  }, [selectedDimension])

  const handleChangeSecond = (second: number) => {
    setSecond(second)
  }

  const handleChartContainerMouseMove = (e: MouseEvent) => {
    isMouseInChartRef.current = true
    if (isControllerMouseMove) {
      setCavansMoveX(0)
      setActiveXAxisIndex(-1)
      return
    }
    const rect = containerRef.current!.getBoundingClientRect()
    const isRealtime = props.viewMode === 'realtime'
    const x = e.clientX - rect.left
    const roundedX = Math.round(x)
    lastMousePosRef.current = { x: roundedX, clientX: e.clientX, clientY: e.clientY }
    let moveX = 0
    if (isRealtime) {
      if (isNeedRealtimeReveal && x > progressLineX) {
        setCavansMoveX(0)
        setActiveXAxisIndex(-1)
        return
      }
      moveX = x
    } else {
      const x = e.clientX - rect.left
      moveX = x
    }
    const videoController = document.getElementById(VIDEO_CONTROLLER_ID) as HTMLDivElement
    const videoControllerRect = videoController.getBoundingClientRect()
    const inControllerArea = e.clientX >= videoControllerRect.left &&
      e.clientX <= videoControllerRect.right &&
      e.clientY >= videoControllerRect.top &&
      e.clientY <= videoControllerRect.bottom
    if (inControllerArea) {
      setCavansMoveX(0)
      setActiveXAxisIndex(-1)
      return
    }
    const nextX = Math.round(moveX)
    setCavansMoveX((prev) => prev === nextX ? prev : nextX)
  }

  const handleChartContainerMouseLeave = () => {
    isMouseInChartRef.current = false
    lastMousePosRef.current = null
    setCavansMoveX(0)
    setActiveXAxisIndex(-1)
  }

  useEventListener('mousemove', handleChartContainerMouseMove, { target: containerRef.current })
  useEventListener('mouseleave', handleChartContainerMouseLeave, { target: containerRef.current })

  useEffect(() => {
    if (props.viewMode !== 'realtime') {
      return
    }
    if (!isMouseInChartRef.current || isControllerMouseMove) {
      return
    }
    const mousePos = lastMousePosRef.current
    if (!mousePos) {
      return
    }
    if (isNeedRealtimeReveal && mousePos.x > progressLineX) {
      return
    }
    const videoController = document.getElementById(VIDEO_CONTROLLER_ID) as HTMLDivElement | null
    if (videoController) {
      const videoControllerRect = videoController.getBoundingClientRect()
      const inControllerArea = mousePos.clientX >= videoControllerRect.left &&
        mousePos.clientX <= videoControllerRect.right &&
        mousePos.clientY >= videoControllerRect.top &&
        mousePos.clientY <= videoControllerRect.bottom
      if (inControllerArea) {
        setCavansMoveX(0)
        setActiveXAxisIndex(-1)
        return
      }
    }
    setCavansMoveX(prev => prev === mousePos.x ? prev : mousePos.x)
  }, [props.viewMode, progressLineX, isNeedRealtimeReveal, isControllerMouseMove])


  const { run: changeSecondThrottle } = useThrottleFn(handleChangeSecond, { wait: 50, leading: true, trailing: true })

  useEffect(() => {
    const width = containerSize?.width || containerRef.current?.getBoundingClientRect().width
    if (!width) {
      return
    }
    const isPortraitSplash = props.orientation === 'portrait' && !isPortraitRegularLayout
    /** 横屏与常规创意竖屏壳体都按 16:9；开屏创意竖屏按固定 300px 宽和实际视频比例自适应 */
    const height =
      isPortraitSplash
        ? PORTRAIT_SPLASH_VIDEO_WIDTH / (videoAspectRatio || 9 / 16) + chartTop + chartBottom
        : width * 9 / 16
    const videoWidth = isPortraitSplash ? PORTRAIT_SPLASH_VIDEO_WIDTH : width - chartLeft - chartRight
    const videoHeight = isPortraitSplash
      ? PORTRAIT_SPLASH_VIDEO_WIDTH / (videoAspectRatio || 9 / 16)
      : height - chartTop - chartBottom
    setChartHeight(prev => prev === height ? prev : height)
    setVideoSize(prev => {
      const next = {
        width: `${videoWidth}px`, height: `${videoHeight}px`,
        left: `${chartLeft}px`, top: `${chartTop}px`
      }
      const isSame = prev.width === next.width &&
        prev.height === next.height &&
        prev.left === next.left &&
        prev.top === next.top
      return isSame ? prev : next
    })
    setContainerRect(prev => {
      if (!containerRef.current) {
        return prev
      }
      const rect = containerRef.current.getBoundingClientRect()
      const isSame = !!prev &&
        prev.width === rect.width &&
        prev.height === rect.height &&
        prev.left === rect.left &&
        prev.top === rect.top
      return isSame ? prev : rect
    })
  }, [containerSize?.width, hostSize?.width, hostSize?.height, chartLeft, chartRight, chartTop, chartBottom, isPortraitRegularLayout, props.orientation, videoAspectRatio])

  useEffect(() => {
    const width = containerSize?.width
    if (!width) {
      return
    }
    const prevWidth = prevContainerWidthRef.current
    prevContainerWidthRef.current = width
    if (prevWidth === undefined) {
      return
    }
    if (Math.abs(prevWidth - width) < 0.5) {
      return
    }
    setIsResizing(true)
    if (resizeIdleTimerRef.current) {
      window.clearTimeout(resizeIdleTimerRef.current)
    }
    resizeIdleTimerRef.current = window.setTimeout(() => {
      setIsResizing(false)
    }, 120)
    videoInstance?.pause()
  }, [containerSize?.width, videoInstance])

  useEffect(() => {
    return () => {
      if (resizeIdleTimerRef.current) {
        window.clearTimeout(resizeIdleTimerRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (videoInstance) {
      videoInstance.on('error', (e) => {
        console.log('videoInstance error', e)
        handleVideoError()
      })
      onError?.([videoInstance])
    }
  }, [videoInstance,])

  useEffect(() => {
    if (videoInstance) {
      videoInstance.volume = vol
    }
  }, [vol, videoInstance])


  useEffect(() => {
    if (videoInstance) {
      videoInstance.on('play', () => {
        onPlay?.([videoInstance])
      })
    }
  }, [videoInstance])

  useLayoutEffect(() => {
    if (!containerSize?.width) {
      return
    }
    axisChartInstance?.resize()
    chartInstance?.resize()
  }, [axisChartInstance, chartInstance, containerSize?.width, chartHeight])

  const handleAxisChartReady = useCallback((chart: unknown) => {
    setAxisChartInstance(chart as EChartsInstance)
  }, [])

  const handleSeriesChartReady = useCallback((chart: unknown) => {
    setChartInstance(chart as EChartsInstance)
  }, [])

  return (
    <>
      <div
        className={styles.videoAnalysisChart}
        ref={containerRef}
        style={(isPortraitSplashLayout || isPortraitRegularLayout) && props.chartWidth ? { width: props.chartWidth } : undefined}
      >
        <div className={styles.chartLayerBox}>
          <CoreChart
            detailMetrics={project.detailMetrics}
            options={chartRenderOptions}
            layerMode='axis'
            className={cls(styles.coreChart, styles.axisChart, props.mode === 'professor' && styles.chartBox)}
            customConfig={handleSetAxisChartCustomConfig}
            onChartReady={handleAxisChartReady}
          />
          <div className={styles.seriesChartClip} style={{ clipPath: seriesClipPath, transition: dragVideoController || isResizing ? 'none' : undefined }}>
            <div className={styles.seriesChartCanvas}>
              <CoreChart
                detailMetrics={project.detailMetrics}
                options={chartRenderOptions}
                layerMode='series'
                className={cls(styles.coreChart, styles.seriesChart, props.mode === 'professor' && styles.chartBox)}
                customConfig={handleSetSeriesChartCustomConfig}
                onChartReady={handleSeriesChartReady}
              />
            </div>
          </div>
        </div>
        <div
          className={cls(
            styles.videoPlayer,
            isPortraitRegularLayout && styles.videoPlayerPortraitRegular
          )}
          style={videoSize}
        >
          {
            erroVideos.includes('video') &&
            <div className={styles.videoFailedCover}>
              <img src={commonUtils.getImagePath('projectDetailPage/video-failed.png')} alt="" />
              <p>{t('dashboard.projectDetailPage.videoView.videoChart.videoFailed')}</p>
            </div>
          }
          <VideoPlayer
            key={`video-player-${componentId}`}
            id={`video-player-${componentId}`}
            url={videoBlobUrl || project.adUrl}
            controls={false}
            currentTime={second}
            noPlayButton={true}
            fitMode={isPortraitRegularLayout ? 'contain' : 'cover'}
            onVideoReady={(instance) => {
              setVideoInstance(instance)
              const media = (instance as any).media as HTMLVideoElement | undefined
              if (media?.videoWidth && media?.videoHeight) {
                setVideoAspectRatio(media.videoWidth / media.videoHeight)
              }
            }}
            onError={() => {
              handleVideoError()
            }}
          />
        </div>
        <VideoController
          id={VIDEO_CONTROLLER_ID}
          videoInstance={videoInstance}
          detailMetrics={project.detailMetrics}
          duration={project.adDuration}
          axisCount={axisCount}
          videoSize={videoSize}
          shouldRefresh={erroVideos.length > 0}
          progress={progress}
          disableProgressTransition={isResizing}
          onProgressChange={setProgress}
          onDragTimeChange={changeSecondThrottle}
          onDragStart={() => {
            setDragVideoController(true)
            videoInstance?.pause()
          }}
          onDragEnd={() => {
            setDragVideoController(false)
          }}
          onControllerMouseMove={() => {
            setIsControllerMouseMove(true)
          }}
          onControllerMouseLeave={() => {
            setIsControllerMouseMove(false)
          }}
          onVolumeChange={(vol) => {
            setVol(vol)
          }}
          needPause={() => {
            videoInstance?.pause()
          }}
          needPlay={() => {
            if (erroVideos.length > 0) {
              videoInstance?.pause()
              return
            }
            videoInstance?.play()
          }}
          onRefresh={() => {
            if (videoInstance) {
              videoInstance.src = props.project.adUrl
            }
            if (!videoInstance) {
              console.error('videoInstance is not ready,so url is not right url')
              return
            }
            /** 重置外部的初始状态 */
            setErroVideos([])
            setSecond(undefined)
          }}
        />
        {progress !== undefined &&
          <ProgressLine
            dimensionLen={selectedDimension.length}
            videoSize={videoSize}
            x={progressLineX}
            disableTransition={dragVideoController || isResizing}
          />}
        <>
          <ProgressDashLine
            linePosition={linePosition}
            x={dashLineX}
            visible={cavansMoveX !== 0}
            videoSize={videoSize}
          />
          <LineDotMap chartInstance={chartInstance}
            selectedDimension={selectedDimension}
            visible={cavansMoveX !== 0}
            duration={props.project.adDuration}
            axisCount={axisCount}
            videoSize={videoSize}
            videoInstance={videoInstance}
            project={props.project}
            parentRect={parentRect}
            lineMoveX={progressLineX}
            dashLineMoveX={dashLineX}
            onXAxisDotsChange={setXAxisDots}
            onActiveXAxisIndexChange={setActiveXAxisIndex}
          />
          <ProgressDashLineViewPanel
            detailMetrics={project.detailMetrics}
            activeXAxisIndex={cavansMoveX !== 0 ? activeXAxisIndex : -1}
            xAxisDots={xAxisDots}
            parentRect={parentRect}
            orientation={props.orientation}
          />
        </>
      </div >
      {isPortraitSplashLayout ? (
        <div className={styles.coverLegendRow}>
          <div
            className={styles.coverTrack}
            style={{
              marginTop: 16,
              width: splashCoverTrackWidth,
              flex: `0 0 ${splashCoverTrackWidth}`,
              paddingLeft: videoSize.left,
            }}
          >
            <VideoAnalysisChartCover
              chartInstance={chartInstance}
              project={props.project}
              orientation={props.orientation}
              thumbnailMode='portrait-splash'
              style={{
                width: splashCoverWidth,
              }}
            />
          </div>
          <LegendDot
            mode='select'
            layout='side-list'
            className={styles.sideLegend}
            onDimensionChange={(dimension) => {
              setSelectedDimension(dimension)
            }}
          />
        </div>
      ) : (
        <>
          <VideoAnalysisChartCover chartInstance={chartInstance} project={props.project}
            orientation={props.orientation}
            thumbnailMode={isPortraitRegularLayout ? 'portrait-splash' : 'default'}
            style={{
              marginTop: 16,
              width: isPortraitRegularLayout
                ? videoSize.width
                : `calc(${containerRect?.width}px - ${(chartConfig.grid as any).left + (chartConfig.grid as any).right}px)`,
              marginLeft: isPortraitRegularLayout ? videoSize.left : undefined,
            }} />
          <LegendDot
            mode='select'
            orientation={props.orientation}
            onDimensionChange={(dimension) => {
              setSelectedDimension(dimension)
            }}
            style={{
              marginTop: 20,
              width: `calc(${containerRect?.width}px - ${(chartConfig.grid as any).left + (chartConfig.grid as any).right}px)`,
            }} />
        </>
      )}
    </>
  )
}

export default VideoAnalysisChart
